import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Group,
  Modal,
  NumberInput,
  SimpleGrid,
  Stack,
  Textarea,
  Text,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconInfoCircle } from '@tabler/icons-react';
import { upsertCashBalance } from '../api/cash-balance';
import { ApiError } from '../api/client';
import { toIsoDate } from '../util/format';
import { useMoneyFormatter } from '../util/money';

interface CashBalanceModalProps {
  opened: boolean;
  onClose: () => void;
  groupId: number;
  primaryCurrency: string;
  initial: {
    cashAmount: string;
    bankAmount: string;
    asOfDate: string;
    notes: string | null;
  } | null;
  onSaved: () => void;
}

const fullSpan = { gridColumn: '1 / -1' };

/**
 * Kassza-egyenleg szerkesztő modal.
 * Layout: mobilon 1 oszlop, desktopon (sm+) 2 oszlop.
 */
export function CashBalanceModal({
  opened,
  onClose,
  groupId,
  primaryCurrency,
  initial,
  onSaved,
}: CashBalanceModalProps) {
  const { money, currencyLabel } = useMoneyFormatter();
  const [cash, setCash] = useState<number | ''>('');
  const [bank, setBank] = useState<number | ''>('');
  const [asOf, setAsOf] = useState<Date | null>(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!opened) return;
    setCash(initial ? Number(initial.cashAmount) : 0);
    setBank(initial ? Number(initial.bankAmount) : 0);
    setAsOf(initial ? new Date(initial.asOfDate) : new Date());
    setNotes(initial?.notes ?? '');
  }, [opened, initial]);

  const canSave =
    typeof cash === 'number' &&
    typeof bank === 'number' &&
    cash >= 0 &&
    bank >= 0 &&
    asOf !== null;

  const handleSave = async () => {
    if (!canSave || typeof cash !== 'number' || typeof bank !== 'number' || !asOf) return;
    setSaving(true);
    try {
      await upsertCashBalance(groupId, {
        cashAmount: cash.toFixed(2),
        bankAmount: bank.toFixed(2),
        asOfDate: toIsoDate(asOf),
        notes: notes.trim() || null,
      });
      notifications.show({ color: 'green', message: 'Kassza-egyenleg mentve.' });
      onSaved();
    } catch (e) {
      notifications.show({
        color: 'red',
        message: e instanceof ApiError ? e.message : String(e),
      });
    } finally {
      setSaving(false);
    }
  };

  const labelForInput = currencyLabel || primaryCurrency;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Kassza-egyenleg szerkesztése"
      overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
      size="lg"
    >
      <Stack>
        <Alert color="blue" variant="light" icon={<IconInfoCircle size={16} />}>
          A kassza-egyenleg a csoport elsődleges devizájában rögzített pillanatkép.
          Az F2e (befizetés) és F2f (kiadás) bevezetése után a számolt egyenleg
          ehhez hozzáadódik / levonódik.
        </Alert>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" verticalSpacing="sm">
          <NumberInput
            label={`Készpénz (${labelForInput})`}
            value={cash}
            onChange={(v) => setCash(typeof v === 'number' ? v : '')}
            min={0}
            decimalScale={2}
            thousandSeparator=" "
            required
          />

          <NumberInput
            label={`Banki egyenleg (${labelForInput})`}
            value={bank}
            onChange={(v) => setBank(typeof v === 'number' ? v : '')}
            min={0}
            decimalScale={2}
            thousandSeparator=" "
            required
          />

          <DateInput
            style={fullSpan}
            label="Vonatkozási dátum (as-of)"
            value={asOf}
            onChange={(v) => setAsOf(v ? new Date(v) : null)}
            valueFormat="YYYY-MM-DD"
            required
          />

          <Textarea
            style={fullSpan}
            label="Megjegyzés (opcionális)"
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
            autosize
            minRows={2}
            maxLength={2000}
          />
        </SimpleGrid>

        {typeof cash === 'number' && typeof bank === 'number' && (
          <Text size="sm" c="dimmed">
            Összesen: <strong>{money(cash + bank)}</strong>
          </Text>
        )}

        <Group justify="end">
          <Button variant="default" onClick={onClose} disabled={saving}>
            Mégse
          </Button>
          <Button onClick={handleSave} loading={saving} disabled={!canSave}>
            Mentés
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
